"use client";

import * as React from "react";
import Link from "next/link";
import { Icon } from "@/components/ui/icon";

import { Header } from "@/app/_components/layout/header";
import { Footer } from "@/app/_components/layout/footer";
import { Container } from "@/app/_components/layout/container";
import { Section } from "@/app/_components/layout/section";
import { cn } from "@/lib/utils";

type Category =
  | "alle"
  | "bfsg"
  | "integration"
  | "preise"
  | "datenschutz"
  | "support";

type Faq = {
  id: string;
  category: Exclude<Category, "alle">;
  question: string;
  answer: string;
  replies?: number;
};

const categoryLabels: Record<Category, string> = {
  alle: "Alle",
  bfsg: "BFSG & Recht",
  integration: "Integration",
  preise: "Preise & Abo",
  datenschutz: "Datenschutz",
  support: "Support",
};

const faqs: ReadonlyArray<Faq> = [
  {
    id: "bfsg-applies",
    category: "bfsg",
    question: "Gilt das BFSG für meine Website?",
    answer:
      "Das Barrierefreiheitsstärkungsgesetz gilt seit Juni 2025 für nahezu alle Websites mit B2C-Angeboten — Onlineshops, Buchungsstrecken, Banking, Reise- und Ticketing-Plattformen sowie digitale Services. Auch B2B-Plattformen, die Endkund:innen Zugang gewähren, sind betroffen. Im Zweifel: lieber konform sein als haften.",
    replies: 12,
  },
  {
    id: "bfsg-penalties",
    category: "bfsg",
    question: "Was passiert bei Verstößen?",
    answer:
      "Behörden können Bußgelder von bis zu €100.000 pro Verstoß verhängen — ohne Vorwarnung. Verbände und Behindertenorganisationen sind zusätzlich klageberechtigt und nutzen das Recht aktiv. Hinzu kommen Abmahnkosten, Reputationsschäden und Umsatzverluste durch Nutzer:innen, die Ihre Seite nicht bedienen können.",
    replies: 8,
  },
  {
    id: "wcag-version",
    category: "bfsg",
    question: "Welche WCAG-Version setzt ClarusPlus um?",
    answer:
      "Wir prüfen nach WCAG 2.2 Level AA — das ist der aktuelle Standard, auf den sich das BFSG bezieht. Sobald WCAG 2.2 EN 301 549 vollständig adoptiert wird, sind Sie damit automatisch konform.",
    replies: 5,
  },
  {
    id: "install-time",
    category: "integration",
    question: "Wie schnell kann ich ClarusPlus integrieren?",
    answer:
      "Ein einziger Code-Schnipsel im <head> oder via Tag-Manager — das war's. Die Integration dauert typischerweise 5–15 Minuten. Wenn Sie schon einmal Google Analytics eingebaut haben, schaffen Sie auch das.",
    replies: 24,
  },
  {
    id: "cms-support",
    category: "integration",
    question: "Funktioniert ClarusPlus mit WordPress / Wix",
    answer:
      "Ja. Da wir ein clientseitiger Code-Schnipsel sind, funktioniert ClarusPlus mit jedem System, das eigenes HTML im <head> erlaubt — das sind praktisch alle gängigen CMS, Shop- und Website-Builder.",
    replies: 17,
  },
  {
    id: "performance",
    category: "integration",
    question: "Belastet das Widget meine Performance?",
    answer:
      "Unser Widget wird asynchron geladen, ist unter 550 KB gzipped und beeinträchtigt den Lighthouse-Performance-Score nicht messbar. Wir gehören bewusst zu den schlankesten A11y-Tools am Markt.",
    replies: 9,
  },
  {
    id: "free-trial",
    category: "preise",
    question: "Gibt es eine kostenlose Testversion?",
    answer:
      "Ja. Sie können ClarusPlus 7 Tage kostenlos und ohne Risiko testen — keine Kreditkarte, keine Einrichtungsgebühr, jederzeit kündbar. Der erste Scan ist sogar ohne Konto möglich.",
    replies: 31,
  },
  {
    id: "plan-change",
    category: "preise",
    question: "Kann ich den Plan jederzeit wechseln?",
    answer:
      "Ja, jederzeit. Upgrades sind sofort aktiv und werden anteilig berechnet. Downgrades werden zum Ende der laufenden Periode wirksam.",
    replies: 4,
  },
  {
    id: "data-location",
    category: "datenschutz",
    question: "Wo werden meine Daten gespeichert?",
    answer:
      "Alle Scan-Daten und Berichte werden ausschließlich auf Servern in Deutschland (Strato AG, Berlin) gespeichert. Wir sind vollständig DSGVO-konform und verarbeiten keine personenbezogenen Daten ohne Rechtsgrundlage.",
    replies: 7,
  },
  {
    id: "gdpr",
    category: "datenschutz",
    question: "Ist ClarusPlus DSGVO-konform?",
    answer:
      "Ja. Wir tracken keine Endnutzer:innen, setzen keine Werbe-Cookies und teilen keine Daten mit Dritten. Eine AV-Vereinbarung stellen wir Ihnen auf Anfrage zur Verfügung.",
    replies: 11,
  },
  {
    id: "support-hours",
    category: "support",
    question: "Wann erreiche ich den Support?",
    answer:
      "Per E-Mail antworten wir Mo–Fr innerhalb eines Werktags. Telefonisch sind wir Mo–Fr von 9 bis 17 Uhr erreichbar. Pro- und Business-Kund:innen erhalten zusätzlich einen direkten Slack-Channel.",
    replies: 3,
  },
  {
    id: "training",
    category: "support",
    question: "Bietet ihr Schulungen oder Workshops an?",
    answer:
      "Ja — von der einstündigen Team-Einweisung bis zum zweitägigen Inhouse-Workshop. Buchbar direkt über uns oder über zertifizierte Partner. Mehr Infos auf Anfrage.",
    replies: 6,
  },
];

const categoryTone: Record<Exclude<Category, "alle">, string> = {
  bfsg: "bg-clarus-rose",
  integration: "bg-clarus-blue",
  preise: "bg-clarus-yellow",
  datenschutz: "bg-clarus-green",
  support: "bg-clarus-purple",
};

export default function FaqPage() {
  const [active, setActive] = React.useState<Category>("alle");
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");

  const filtered = faqs.filter((f) => {
    const matchCat = active === "alle" || f.category === active;
    const matchQuery =
      !query ||
      f.question.toLowerCase().includes(query.toLowerCase()) ||
      f.answer.toLowerCase().includes(query.toLowerCase());
    return matchCat && matchQuery;
  });

  const categories: ReadonlyArray<Category> = [
    "alle",
    "bfsg",
    "integration",
    "preise",
    "datenschutz",
    "support",
  ];

  return (
    <>
      <Header />
      <main className="flex-1">
        <Section tone="default" spacing="xl" ariaLabel="Häufige Fragen" className="pt-12 md:pt-20">
          <Container size="narrow">
            <div className="text-center">
              <h1 className="text-display-xl text-clarus-dark">
                Antworten aus der Community.
              </h1>
            </div>

            <div className="mt-10 relative">
              <Icon
                name="search"
                className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-[20px] text-clarus-dark/55"
              />
              <label htmlFor="faq-search" className="sr-only">
                Frage durchsuchen
              </label>
              <input
                id="faq-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Frage durchsuchen …"
                className="h-12 w-full rounded-pill border-[1.5px] border-clarus-dark bg-white pl-12 pr-5 text-body text-clarus-dark placeholder:text-clarus-dark/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2"
              />
            </div>

            {/* On mobile each category becomes its own pill chip that
                wraps naturally instead of forcing a horizontal scroller.
                On md+ we fall back to the single segmented control. */}
            <div
              role="tablist"
              aria-label="Kategorien"
              className="mt-6 flex flex-wrap justify-center gap-2 md:mx-auto md:w-max md:flex-nowrap md:gap-1 md:rounded-pill md:border-[1.5px] md:border-clarus-dark md:bg-white md:p-1.5"
            >
              {categories.map((cat) => {
                const isActive = active === cat;
                return (
                  <button
                    key={cat}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActive(cat)}
                    className={cn(
                      "rounded-pill border-[1.5px] px-3.5 py-1.5 text-cta transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple md:border-0 md:px-4",
                      isActive
                        ? "border-clarus-dark bg-clarus-dark text-clarus-yellow"
                        : "border-clarus-dark/30 bg-white text-clarus-dark/70 hover:border-clarus-dark hover:text-clarus-dark md:bg-transparent",
                    )}
                  >
                    {categoryLabels[cat]}
                  </button>
                );
              })}
            </div>

            <ul className="mt-8 space-y-3">
              {filtered.length === 0 ? (
                <li className="rounded-card border-[1.5px] border-clarus-dark bg-white p-6 text-center text-body text-clarus-dark/75">
                  Keine passenden Fragen gefunden. Schreiben Sie uns gerne via{" "}
                  <Link
                    href="/kontakt"
                    className="font-bold text-clarus-dark underline underline-offset-4"
                  >
                    Kontakt
                  </Link>
                  .
                </li>
              ) : (
                filtered.map((f) => {
                  const isOpen = openId === f.id;
                  return (
                    <li
                      key={f.id}
                      className="group/faq rounded-card border-[1.5px] border-clarus-dark bg-white"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenId(isOpen ? null : f.id)}
                        aria-expanded={isOpen}
                        aria-controls={`faq-${f.id}`}
                        className="flex w-full items-start gap-4 p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple rounded-card"
                      >
                        <span
                          className={cn(
                            "shrink-0 rounded-pill border-[1.5px] border-clarus-dark px-3 py-1 text-eyebrow uppercase text-clarus-dark",
                            categoryTone[f.category],
                          )}
                        >
                          {categoryLabels[f.category]}
                        </span>
                        <span className="flex-1">
                          <span className="block text-cta font-bold text-clarus-dark">
                            {f.question}
                          </span>
                        </span>
                        <span
                          aria-hidden="true"
                          className={cn(
                            "mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-[1.5px] border-clarus-dark text-clarus-dark transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none group-hover/faq:rotate-90",
                            isOpen && "rotate-90",
                          )}
                        >
                          <Icon name="add" className="text-[16px]" />
                        </span>
                      </button>
                      <div
                        id={`faq-${f.id}`}
                        className={cn(
                          "grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none group-hover/faq:grid-rows-[1fr]",
                          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                        )}
                      >
                        <div className="overflow-hidden">
                          <p className="text-body px-5 pb-5 text-clarus-dark/80">
                            {f.answer}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })
              )}
            </ul>

            <div className="mt-12 rounded-card bg-clarus-dark p-8 md:p-10 text-clarus-yellow">
              <h2 className="text-display-md text-clarus-yellow">
                Antwort nicht dabei?
              </h2>
              <p className="text-body mt-3 max-w-md text-clarus-purple">
                Schreiben Sie uns direkt — meist antworten wir innerhalb eines
                Werktags, oft schneller.
              </p>
              <div className="mt-6">
                <Link
                  href="/kontakt"
                  className="inline-flex h-12 items-center justify-center rounded-pill bg-clarus-yellow px-6 text-cta text-clarus-dark transition-colors hover:bg-clarus-yellow/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2 focus-visible:ring-offset-clarus-dark"
                >
                  Frage stellen
                </Link>
              </div>
            </div>
          </Container>
        </Section>
      </main>
      <Footer />
    </>
  );
}
